// Sanitizer for ORG-AUTHORED HTML rendered on public tenant sites and in the
// admin canvas (tranche-2 gap 7). Threat: the admin session cookie spans
// *.staty.io, so a script smuggled into a tenant page's authored content runs
// same-site against api.staty.io for every visiting admin. Defense in depth
// with the cookie-mutation origin gate (middleware/csrfOrigin.ts).
//
// Profile: keep the legacy content model working — text markup, images,
// styles, and provider iframes (YouTube/Vimeo/Google Maps embeds are first-
// class in both Quill-era html and the embed block) — strip scripts, event
// handlers, and javascript: URLs. Iframe sources are allow-listed by host.
import DOMPurify from 'dompurify';

const IFRAME_HOST_ALLOW = [
  /(^|\.)youtube\.com$/i,
  /(^|\.)youtube-nocookie\.com$/i,
  /(^|\.)vimeo\.com$/i,
  /(^|\.)google\.com$/i, // maps embeds (google.com/maps, maps.google.com)
];

let hooked = false;
function ensureHook() {
  if (hooked) return;
  hooked = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'IFRAME') {
      let ok = false;
      try {
        const host = new URL(node.getAttribute('src') || '').hostname;
        ok = IFRAME_HOST_ALLOW.some((re) => re.test(host));
      } catch (e) { ok = false; }
      if (!ok) node.remove();
    }
    // target=_blank hygiene
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

export function sanitizeAuthoredHtml(html: string | null | undefined): string {
  if (!html) return '';
  // Renderers are client-side; in a DOM-less context (node test envs) pass
  // through — the server never injects this HTML into a page itself.
  // (globalThis form keeps this compilable under the API's DOM-less tsconfig.)
  if (typeof (globalThis as any).window === 'undefined') return html;
  ensureHook();
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target'],
  });
}
